import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tag, Plus, Pencil, Trash2, Users, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_PRESETS = [
  { key: 'emerald', hex: '#10b981', label: 'Emerald' },
  { key: 'blue', hex: '#3b82f6', label: 'Blue' },
  { key: 'amber', hex: '#f59e0b', label: 'Amber' },
  { key: 'rose', hex: '#f43f5e', label: 'Rose' },
  { key: 'purple', hex: '#8b5cf6', label: 'Purple' },
  { key: 'slate', hex: '#64748b', label: 'Slate' },
];

const emptyForm = { name: '', color: '#10b981', description: '' };

const TagManagement = () => {
  const { user } = useAuth();
  const canEdit = user?.role === 'Admin' || user?.role === 'Staff';
  const [tags, setTags] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [tagMap, setTagMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Create / edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Athletes dialog (assign tag to athletes)
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTag, setAssignTag] = useState(null);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignBusy, setAssignBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [t, a, m] = await Promise.all([
        axios.get(`${API}/tags`),
        axios.get(`${API}/athletes?status=all`),
        axios.get(`${API}/tags/map`),
      ]);
      setTags(t.data || []);
      setAthletes(a.data || []);
      setTagMap(m.data || {});
    } catch {
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (tag) => {
    setEditingId(tag.id);
    setForm({ name: tag.name || '', color: tag.color || '#10b981', description: tag.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) { toast.error('Tag name is required'); return; }
    setSaving(true);
    try {
      const payload = { name, color: form.color || null, description: form.description || null };
      if (editingId) {
        await axios.put(`${API}/tags/${editingId}`, payload);
        toast.success('Tag updated');
      } else {
        await axios.post(`${API}/tags`, payload);
        toast.success('Tag created');
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save tag');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tag) => {
    if (!window.confirm(`Delete tag "${tag.name}"? This removes it from ${tag.athlete_count} athlete(s) but does NOT delete the athletes.`)) return;
    try {
      await axios.delete(`${API}/tags/${tag.id}`);
      toast.success(`Tag "${tag.name}" deleted`);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete tag');
    }
  };

  const openAssign = (tag) => {
    setAssignTag(tag);
    setAssignSearch('');
    setAssignOpen(true);
  };

  const assignedAthletes = useMemo(() => {
    if (!assignTag) return new Set();
    return new Set(
      Object.entries(tagMap)
        .filter(([, tagIds]) => tagIds.includes(assignTag.id))
        .map(([aid]) => aid)
    );
  }, [assignTag, tagMap]);

  const toggleAthleteTag = async (athleteId) => {
    if (!assignTag || assignBusy) return;
    const isAssigned = assignedAthletes.has(athleteId);
    setAssignBusy(true);
    try {
      await axios.post(`${API}/tags/bulk-assign`, {
        athlete_ids: [athleteId],
        tag_ids: [assignTag.id],
        mode: isAssigned ? 'remove' : 'add',
      });
      // Optimistic map update
      setTagMap(prev => {
        const next = { ...prev };
        const current = new Set(next[athleteId] || []);
        if (isAssigned) current.delete(assignTag.id); else current.add(assignTag.id);
        next[athleteId] = [...current];
        return next;
      });
      // Refresh counts
      const res = await axios.get(`${API}/tags`);
      setTags(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setAssignBusy(false);
    }
  };

  const filteredTags = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tags;
    return tags.filter(t =>
      (t.name || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    );
  }, [tags, search]);

  const filteredAssignAthletes = useMemo(() => {
    const q = assignSearch.trim().toLowerCase();
    const base = athletes.filter(a => (a.status || 'active') === 'active' || assignedAthletes.has(a.id));
    if (!q) return base;
    return base.filter(a =>
      (a.full_name || '').toLowerCase().includes(q) ||
      (a.squad || '').toLowerCase().includes(q) ||
      (a.position || '').toLowerCase().includes(q)
    );
  }, [athletes, assignSearch, assignedAthletes]);

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="tag-management-page">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="page-title">
              Athlete Tags
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Create reusable groupings to target training, programs, and communication.
            </p>
          </div>
          {canEdit && (
            <Button onClick={openCreate} className="bg-pfap-green hover:bg-pfap-green/90 text-white" data-testid="create-tag-btn">
              <Plus size={16} className="mr-1.5" /> New Tag
            </Button>
          )}
        </div>

        <div className="relative max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search tags…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="tag-search-input" />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1,2,3].map(i => <div key={i} className="h-[120px] rounded-xl bg-gray-100 dark:bg-gray-700 animate-pulse" />)}
          </div>
        ) : filteredTags.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              <Tag size={28} className="mx-auto mb-2 opacity-40" />
              {tags.length === 0 ? 'No tags yet — create your first grouping.' : 'No tags match your search.'}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="tags-grid">
            {filteredTags.map(tag => (
              <Card key={tag.id} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid={`tag-card-${tag.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0 border border-white/40"
                        style={{ background: tag.color || '#64748b' }}
                        aria-hidden
                      />
                      <CardTitle className="text-base truncate" data-testid={`tag-name-${tag.id}`}>{tag.name}</CardTitle>
                    </div>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0" data-testid={`tag-count-${tag.id}`}>
                      <Users size={10} className="mr-1" />
                      {tag.athlete_count}
                    </Badge>
                  </div>
                  {tag.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">{tag.description}</p>
                  )}
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="flex items-center gap-1.5">
                    {canEdit && (
                      <Button size="sm" variant="outline" className="h-7 text-xs flex-1" onClick={() => openAssign(tag)} data-testid={`assign-tag-${tag.id}`}>
                        <Users size={12} className="mr-1" /> Manage Athletes
                      </Button>
                    )}
                    {canEdit && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(tag)} data-testid={`edit-tag-${tag.id}`}>
                        <Pencil size={13} />
                      </Button>
                    )}
                    {canEdit && (
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700" onClick={() => handleDelete(tag)} data-testid={`delete-tag-${tag.id}`}>
                        <Trash2 size={13} />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create / Edit dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md" data-testid="tag-dialog">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Tag' : 'New Tag'}</DialogTitle>
              <DialogDescription>Create a reusable grouping you can assign to athletes.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Name *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Trial Players" data-testid="tag-name-input" />
              </div>
              <div>
                <Label>Color</Label>
                <div className="flex flex-wrap gap-1.5 mt-1" data-testid="tag-color-swatches">
                  {COLOR_PRESETS.map(c => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, color: c.hex }))}
                      className={`w-7 h-7 rounded-full border-2 transition-all ${form.color === c.hex ? 'border-gray-900 dark:border-white scale-110' : 'border-transparent hover:border-gray-400'}`}
                      style={{ background: c.hex }}
                      title={c.label}
                      data-testid={`tag-color-${c.key}`}
                    />
                  ))}
                  <Input
                    type="color"
                    value={form.color}
                    onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                    className="w-10 h-7 p-0 border-gray-200 dark:border-gray-700"
                    data-testid="tag-color-input"
                  />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional — what this tag represents"
                  rows={2}
                  data-testid="tag-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="tag-cancel-btn">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-pfap-green hover:bg-pfap-green/90 text-white" data-testid="tag-save-btn">
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create tag'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign athletes dialog */}
        <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
          <DialogContent className="max-w-lg" data-testid="assign-tag-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ background: assignTag?.color || '#64748b' }}
                />
                Manage athletes — {assignTag?.name}
              </DialogTitle>
              <DialogDescription>
                Toggle athletes to add or remove them from this tag. Inactive athletes are hidden unless already tagged.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input placeholder="Search athletes…" value={assignSearch} onChange={e => setAssignSearch(e.target.value)} className="pl-9 h-9" data-testid="assign-athlete-search" />
              </div>
              <div className="max-h-[55vh] overflow-y-auto border rounded-lg divide-y divide-gray-100 dark:divide-gray-700" data-testid="assign-athlete-list">
                {filteredAssignAthletes.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 p-3">No athletes found.</p>
                )}
                {filteredAssignAthletes.map(a => {
                  const isAssigned = assignedAthletes.has(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => toggleAthleteTag(a.id)}
                      disabled={assignBusy}
                      className={`w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors ${isAssigned ? 'bg-pfap-green/10 dark:bg-pfap-green/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'} ${assignBusy ? 'opacity-60' : ''}`}
                      data-testid={`assign-toggle-${a.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {a.full_name}
                          {a.status === 'inactive' && (
                            <Badge variant="outline" className="ml-2 text-[9px] px-1 py-0 text-gray-500">inactive</Badge>
                          )}
                        </p>
                        <p className="text-[11px] text-gray-400">{a.position}{a.squad ? ` · ${a.squad}` : ''}</p>
                      </div>
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isAssigned ? 'bg-pfap-green border-pfap-green' : 'border-gray-300 dark:border-gray-600'}`}
                      >
                        {isAssigned && <span className="text-white text-[10px] leading-none">✓</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignOpen(false)} data-testid="assign-done-btn">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TagManagement;
